import Seo from "@/components/Seo";
import React, { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { M } from "@/lib/motionStatic";
import { ArrowRight, Presentation, Download } from "lucide-react";
import { CORE_SLIDES, APPENDIX_SLIDES, DECK, PITCH_META } from "@/data/pitchContent";
import { SlideRenderer } from "@/pages/pitch/SlideRenderer";
import { SLIDE_W, SLIDE_H } from "@/pages/pitch/SlideKit";

const ResponsiveSlide = ({ slide, index, total }) => {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.8);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const compute = () => setScale(el.getBoundingClientRect().width / SLIDE_W);
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="w-full" style={{ height: SLIDE_H * scale }}>
      <div
        className="rounded-[1.5rem] overflow-hidden"
        style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})`, transformOrigin: "top left", boxShadow: "0 40px 90px -45px rgba(11,11,15,0.4)", border: "1px solid rgba(11,11,15,0.06)" }}
      >
        <SlideRenderer slide={slide} index={index} total={total} showMeta={false} />
      </div>
    </div>
  );
};

const Section = ({ slide, index, total, alt }) => (
  <section className="py-14 sm:py-20" style={{ background: alt ? DECK.offwhite : "#fff" }}>
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <M.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="flex items-center gap-3 mb-5">
          <span className="font-display text-[14px] font-extrabold px-2.5 py-1 rounded-full" style={{ background: DECK.accent, color: DECK.ink }}>
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="text-[13px] font-bold uppercase tracking-[0.22em]" style={{ color: DECK.muted }}>
            {slide.kicker || "Cover"}
          </span>
        </div>
        <ResponsiveSlide slide={slide} index={index} total={total} />
      </M.div>
    </div>
  </section>
);

export default function InvestorMicrosite() {
  const navigate = useNavigate();
  const total = CORE_SLIDES.length + APPENDIX_SLIDES.length;

  React.useEffect(() => {
    document.title = "WorkPay — Investor Overview";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "WorkPay investor overview — the payment trust layer for service businesses. Vendors deserve power, customers deserve protection.");
  }, []);

  return (
    <main data-testid="investor-microsite" className="pt-24">
      <Seo path="/investor" title="WorkPay — Investor Overview" noindex />
      {/* Hero */}
      <section className="relative overflow-hidden py-16 sm:py-24" style={{ background: DECK.offwhite }}>
        <div className="absolute rounded-full blur-3xl" style={{ height: 420, width: 420, background: DECK.accent, opacity: 0.22, top: -120, right: -60 }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.28em]" style={{ color: DECK.charcoal }}>
            <span className="h-[3px] w-7 rounded-full" style={{ background: DECK.accent }} /> Investor Overview
          </span>
          <h1 className="font-display font-extrabold tracking-tight leading-[1.02] mt-6 text-5xl sm:text-6xl lg:text-7xl" style={{ color: DECK.ink }}>
            The trust layer for{" "}
            <span className="relative inline-block">
              <span className="absolute left-0 right-0 bottom-[0.1em] h-[0.4em] rounded-sm" style={{ background: DECK.accent }} />
              <span className="relative">service payments.</span>
            </span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl font-semibold max-w-2xl" style={{ color: DECK.charcoal }}>
            Vendors deserve power. Customers deserve protection. {PITCH_META.brand} gives both.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button data-testid="investor-open-deck" onClick={() => navigate("/pitch")} className="flex items-center justify-center gap-2 rounded-full px-7 h-12 font-bold" style={{ background: DECK.accent, color: DECK.ink }}>
              <Presentation className="h-5 w-5" /> Open presentation
            </button>
            <button data-testid="investor-download" onClick={() => navigate("/pitch/print")} className="flex items-center justify-center gap-2 rounded-full px-7 h-12 font-bold" style={{ background: "#fff", color: DECK.ink, border: "1px solid rgba(11,11,15,0.14)" }}>
              <Download className="h-5 w-5" /> Download PDF
            </button>
          </div>
        </div>
      </section>

      {/* Core slides as scroll narrative */}
      {CORE_SLIDES.map((sl, i) => (
        <Section key={sl.id} slide={sl} index={i} total={total} alt={i % 2 === 1} />
      ))}

      {/* Appendix */}
      {APPENDIX_SLIDES.map((sl, i) => (
        <Section key={sl.id} slide={sl} index={CORE_SLIDES.length + i} total={total} alt={(CORE_SLIDES.length + i) % 2 === 1} />
      ))}

      {/* Closing CTA */}
      <section className="py-20 sm:py-28" style={{ background: DECK.ink }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
            Let’s rebuild how service businesses{" "}
            <span className="relative inline-block">
              <span className="absolute left-0 right-0 bottom-[0.1em] h-[0.4em] rounded-sm" style={{ background: DECK.accent }} />
              <span className="relative">get paid.</span>
            </span>
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button data-testid="investor-cta-deck" onClick={() => navigate("/pitch")} className="flex items-center justify-center gap-2 rounded-full px-7 h-12 font-bold" style={{ background: DECK.accent, color: DECK.ink }}>
              View the full deck <ArrowRight className="h-5 w-5" />
            </button>
            <a href={`mailto:${PITCH_META.contact}`} data-testid="investor-contact" className="flex items-center justify-center gap-2 rounded-full px-7 h-12 font-bold text-white" style={{ border: "1px solid rgba(255,255,255,0.22)" }}>
              Contact us
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
